import React, { useState, useEffect } from "react";
import { fetchWatchlist } from "../../redux/slice/watchlistSlice";
import { useDispatch, useSelector } from "react-redux";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./Workbench.css";
import all_tickers from "../../assets/nasdaq_tickers.json";
import BacktradeOptions from "./BackTradeOptions";
import uptriangle from "../../assets/uptriangle.svg";
import downtriangle from "../../assets/downtriangle.svg";
import {
  clearStockDetails,
  fetchStockDetails,
} from "../../redux/slice/tickerSlice";
import {
  fetchAnnotations,
  addAnnotations,
} from "../../redux/slice/workbenchSlice";
import WorkSpaceTopLeft from "./WorkSpaceTopLeft";
import Notes from "./Notes";
import HorizontalNews from "./News";

function Workbench() {
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const news = useSelector((state) => state.ticker?.stock?.news);
  const historicalD = useSelector((state) => state.ticker.historical_data);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [activeTab, setActiveTab] = useState("News");
  const [ticker, setTicker] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const watchlist = useSelector((state) => state?.watchlist?.stocks);
  const annotations = useSelector((state) => state.workbench?.annotations);
  const { loading } = useSelector((state) => state.ticker);
  const [sessionAnnotations, setSessionAnnotations] = useState([]);
  const [actionHistory, setActionHistory] = useState([]);
  const [deletes, setDeletes] = useState(0);
  const [noteDeletes, setNoteDeletes] = useState(0);

  useEffect(() => {
    dispatch(fetchAnnotations());
    dispatch(fetchWatchlist());
    dispatch(clearStockDetails());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (annotations) {
      setSessionAnnotations(annotations);
    }
  }, [annotations]);

  useEffect(() => {
    setActiveTab("News");
  }, [ticker]);

  const handleSessionSave = async () => {
    const data = {
      annotations: sessionAnnotations,
    };

    try {
      const resultAction = await dispatch(addAnnotations(data));
      if (addAnnotations.fulfilled.match(resultAction)) {
        alert("Annotations added successfully!");
        dispatch(fetchAnnotations());
      } else {
        alert(`Error: ${resultAction.payload || "Failed to add annotations."}`);
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong while adding annotations.");
    }
    setActionHistory([]);
    setDeletes(0);
    setNoteDeletes(0);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value.toUpperCase();
    setSearchValue(value);
    if (value) {
      const filtered = all_tickers.filter((ticker) => ticker.startsWith(value));
      setFilteredOptions(filtered);
      setDropdownVisible(true);
    } else {
      setFilteredOptions([]);
      setDropdownVisible(false);
    }
  };

  const handleOptionSelect = (option) => {
    setSearchValue(option);
    setDropdownVisible(false);
  };

  const handleStockFetch = async (stock) => {
    setTicker(stock);
    dispatch(fetchStockDetails(stock));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "News":
        return <HorizontalNews news={news} />;
      case "Financials":
        return <div>Content for Financials</div>;
      case "Economics":
        return <div>Content for Economics</div>;
      case "Events":
        return <div>Content for Events</div>;
      case "Quarterly Results":
        return <div>Content for Quarterly Results</div>;
      case "Notes":
        return (
          <Notes
            ticker={ticker}
            sessionAnnotations={sessionAnnotations}
            setSessionAnnotations={setSessionAnnotations}
            noteDeletes={noteDeletes}
            setNoteDeletes={setNoteDeletes}
            actionHistory={actionHistory}
            setActionHistory={setActionHistory}
          />
        );
      default:
        return <div>Select a tab to see content</div>;
    }
  };

  return (
    <div className="workbench">
      <div className="d-flex" style={{ height: "calc(100vh - 56px)" }}>
        <div
          className="sidebar bg-secondary text-white rounded p-2"
          style={{ width: "16%", margin: "3px" }}
        >
          <div className="search-container position-relative mb-3">
            <input
              type="text"
              placeholder="Search for Tickers"
              name="ticker"
              value={searchValue}
              onChange={handleSearchChange}
              className="search-input form-control pl-5 text-white"
            />
            <Search
              className="search-icon position-absolute"
              size={20}
              style={{
                left: "85%",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
              }}
              onClick={() => handleStockFetch(searchValue)}
            />

            {isDropdownVisible && (
              <ul className="dropdown-menu show uls">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <li
                      key={index}
                      className="dropdown-item"
                      onClick={() => handleOptionSelect(option)}
                      style={{ cursor: "pointer" }}
                    >
                      {option}
                    </li>
                  ))
                ) : (
                  <li className="dropdown-item text-muted">No matches found</li>
                )}
              </ul>
            )}
          </div>
          <div className="watchlist-container">
            <div
              className={`watchlist-menu ${showWatchlist ? "show" : ""}`}
              onClick={() => setShowWatchlist(!showWatchlist)}
            >
              <span>Watchlist</span>
              <span className="arrow-icon">&#9660;</span>
            </div>

            <div
              className={`watchlist-dropdown ${showWatchlist ? "show" : ""}`}
            >
              {watchlist && watchlist.length !== 0 ? (
                <ul className="list-group">
                  {watchlist.map((item, index) => (
                    <li
                      key={index}
                      className="list-group-item"
                      onClick={() => handleStockFetch(item.ticker)}
                    >
                      <div>{item.ticker}</div>
                      <div>
                        <span>${item.latest_price.toFixed(2)}</span>
                        <img
                          src={item.pchange > 0 ? uptriangle : downtriangle}
                          height={13}
                          width={13}
                          alt="change"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div>No Stocks in your watchlist</div>
              )}
            </div>
          </div>
        </div>

        <div
          className="workspace flex-grow-1 d-flex flex-column"
          style={{ margin: "3px" }}
        >
          <div
            className="d-flex justify-content-evenly"
            style={{ height: "69%" }}
          >
            <WorkSpaceTopLeft
              historicalData={historicalD}
              ticker={ticker}
              loading={loading}
              sessionAnnotations={sessionAnnotations}
              setSessionAnnotations={setSessionAnnotations}
              actionHistory={actionHistory}
              setActionHistory={setActionHistory}
              deletes={deletes}
              setDeletes={setDeletes}
            />
            <div
              className="workspace-top-right d-flex flex-column"
              style={{ width: "19%" }}
            >
              <div className="d-flex flex-column gap-3 bg-dark mx-2 my-1 rounded p-3">
                <button
                  className="p-1 border border-white text-center rounded-pill"
                  onClick={() => {
                    const hasUnsavedChanges = Object.values(actionHistory).some(
                      (arr) => arr.length > 0
                    );

                    if (
                      hasUnsavedChanges ||
                      deletes !== 0 ||
                      noteDeletes !== 0
                    ) {
                      const confirmLeave = window.confirm(
                        "You have unsaved changes. Do you want to leave?"
                      );
                      if (confirmLeave) {
                        setActionHistory({});
                        setSessionAnnotations([]);
                        setDeletes(0);
                        setNoteDeletes(0);
                        navigate("/compare");
                      }
                    } else {
                      navigate("/compare");
                    }
                  }}
                >
                  Compare
                </button>
              </div>
              <div className="d-flex flex-column gap-3 bg-dark mx-2 my-1 rounded p-3">
                <BacktradeOptions />
              </div>
              <div className="d-flex flex-column gap-2 bg-dark mx-2 my-1 rounded p-3">
                <button
                  className="btn btn-outline-light"
                  onClick={handleSessionSave}
                >
                  Save
                </button>
                <button className="btn btn-outline-light">Share</button>
                <button className="btn btn-outline-light">Export</button>
                <button className="btn btn-outline-light">Print</button>
                <button
                  className="btn btn-outline-light"
                  onClick={() => {
                    const hasUnsavedChanges = Object.values(actionHistory).some(
                      (arr) => arr.length > 0
                    );

                    if (
                      hasUnsavedChanges ||
                      deletes !== 0 ||
                      noteDeletes !== 0
                    ) {
                      const confirmLeave = window.confirm(
                        "You have unsaved changes. Do you want to leave?"
                      );
                      if (confirmLeave) {
                        setActionHistory({});
                        setSessionAnnotations([]);
                        setDeletes(0);
                        setNoteDeletes(0);
                        navigate("/discover");
                      }
                      // If canceled: stay on the page
                    } else {
                      navigate("/discover");
                    }
                  }}
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
          <div className="workspace-bottom bg-dark text-white rounded">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "News" ? "active" : ""}`}
                  onClick={() => setActiveTab("News")}
                >
                  News
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "Financials" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("Financials")}
                >
                  Financials
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "Economics" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("Economics")}
                >
                  Economics
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "Events" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("Events")}
                >
                  Events
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "Quarterly Results" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("Quarterly Results")}
                >
                  Quarterly Results
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "Notes" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("Notes")}
                >
                  Notes
                </button>
              </li>
            </ul>
            <div className="tab-con">{renderTabContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Workbench;
